import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Search, Star, MapPin, Clock, Filter, SlidersHorizontal, Heart, X } from 'lucide-react';
import { doctorService, specialtyService, transformDoctor, transformSpecialty, getLocalizedValue } from '@/services/api';
import { useFavorites } from '@/contexts/FavoritesContext';
import { toast } from 'sonner';

// Mock data for when API is unavailable
const getMockDoctors = (lang) => [
  { id: 1, name: lang === 'ar' ? 'د. سارة جونسون' : 'Dr. Sarah Johnson', specialty: { id: 1, name: lang === 'ar' ? 'أمراض القلب' : 'Cardiology' }, rating: 4.9, reviews_count: 127, experience_years: 15, address: lang === 'ar' ? 'المركز الطبي' : 'Downtown Medical Center', image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop', available: true, consultation_fee: 150, city: 'Damascus' },
  { id: 2, name: lang === 'ar' ? 'د. أحمد حسن' : 'Dr. Ahmed Hassan', specialty: { id: 2, name: lang === 'ar' ? 'طب الأعصاب' : 'Neurology' }, rating: 4.8, reviews_count: 98, experience_years: 12, address: lang === 'ar' ? 'مستشفى المدينة' : 'City Hospital', image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200&h=200&fit=crop', available: true, consultation_fee: 120, city: 'Aleppo' },
  { id: 3, name: lang === 'ar' ? 'د. إيميلي تشين' : 'Dr. Emily Chen', specialty: { id: 3, name: lang === 'ar' ? 'طب العيون' : 'Ophthalmology' }, rating: 4.9, reviews_count: 156, experience_years: 10, address: lang === 'ar' ? 'عيادة الرؤية' : 'Vision Care Clinic', image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=200&h=200&fit=crop', available: false, consultation_fee: 100, city: 'Damascus' },
  { id: 4, name: lang === 'ar' ? 'د. مايكل براون' : 'Dr. Michael Brown', specialty: { id: 4, name: lang === 'ar' ? 'جراحة العظام' : 'Orthopedics' }, rating: 4.7, reviews_count: 89, experience_years: 18, address: lang === 'ar' ? 'مركز الطب الرياضي' : 'Sports Medicine Center', image: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=200&h=200&fit=crop', available: true, consultation_fee: 200, city: 'Homs' },
  { id: 5, name: lang === 'ar' ? 'د. فاطمة الرشيد' : 'Dr. Fatima Al-Rashid', specialty: { id: 5, name: lang === 'ar' ? 'طب الأطفال' : 'Pediatrics' }, rating: 4.9, reviews_count: 203, experience_years: 14, address: lang === 'ar' ? 'مستشفى الأطفال' : 'Children Hospital', image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop', available: true, consultation_fee: 80, city: 'Damascus' },
  { id: 6, name: lang === 'ar' ? 'د. جيمس ويلسون' : 'Dr. James Wilson', specialty: { id: 6, name: lang === 'ar' ? 'الأمراض الجلدية' : 'Dermatology' }, rating: 4.6, reviews_count: 72, experience_years: 8, address: lang === 'ar' ? 'عيادة العناية بالبشرة' : 'Skin Care Clinic', image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200&h=200&fit=crop', available: true, consultation_fee: 90, city: 'Latakia' },
];

const getMockSpecialties = (lang) => [
  { id: 1, name: lang === 'ar' ? 'أمراض القلب' : 'Cardiology' },
  { id: 2, name: lang === 'ar' ? 'طب الأعصاب' : 'Neurology' },
  { id: 3, name: lang === 'ar' ? 'طب العيون' : 'Ophthalmology' },
  { id: 4, name: lang === 'ar' ? 'جراحة العظام' : 'Orthopedics' },
  { id: 5, name: lang === 'ar' ? 'طب الأطفال' : 'Pediatrics' },
  { id: 6, name: lang === 'ar' ? 'الأمراض الجلدية' : 'Dermatology' },
];

const CITIES = [
  { id: 'all', name_en: 'All Cities', name_ar: 'جميع المدن' },
  { id: 'damascus', name_en: 'Damascus', name_ar: 'دمشق' },
  { id: 'aleppo', name_en: 'Aleppo', name_ar: 'حلب' },
  { id: 'homs', name_en: 'Homs', name_ar: 'حمص' },
  { id: 'latakia', name_en: 'Latakia', name_ar: 'اللاذقية' },
  { id: 'hama', name_en: 'Hama', name_ar: 'حماة' },
];

const DoctorsPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get('specialty') || 'all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [minRating, setMinRating] = useState(0);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [sortBy, setSortBy] = useState('rating');
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  const { isFavorite, toggleFavorite } = useFavorites();
  const lang = i18n.language || 'ar';

  // Initialize search from URL params
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  // Fetch doctors - fetch all doctors once, filter on client side
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('Fetching doctors...');
        
        // Always fetch all doctors - API doesn't support server-side specialty filtering
        const [doctorsRes, specialtiesRes] = await Promise.all([
          doctorService.getAllDoctors(),
          specialtyService.getAll(),
        ]);
        
        console.log('API Response:', doctorsRes.data);
        
        // Handle different API response structures
        // /all-doctors returns: { success, data: [...doctors], message }
        let rawDoctors = [];
        const resData = doctorsRes.data.data || doctorsRes.data;
        if (Array.isArray(resData)) {
          rawDoctors = resData;
        } else if (resData && Array.isArray(resData.doctors)) {
          rawDoctors = resData.doctors;
        }
        
        const rawSpecialties = specialtiesRes.data.data || specialtiesRes.data || [];
        
        if (rawDoctors.length > 0) {
          // Transform doctors without modifying their specialty
          const transformedDoctors = rawDoctors.map(d => transformDoctor(d, lang));
          setDoctors(transformedDoctors);
        } else {
          setDoctors(getMockDoctors(lang));
        }
        
        if (rawSpecialties.length > 0) {
          setSpecialties(rawSpecialties.map(s => transformSpecialty(s, lang)));
        } else {
          setSpecialties(getMockSpecialties(lang));
        }
      } catch (error) {
        console.error('Failed to fetch data, using mock data:', error);
        setDoctors(getMockDoctors(lang));
        setSpecialties(getMockSpecialties(lang));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [lang]); // Only re-fetch when language changes, not specialty

  // Apply filters - including specialty filtering on client side
  // Note: API doesn't provide specialty info per doctor, so specialty filter only works with mock data
  const filteredDoctors = doctors
    .filter(doctor => {
      // Search across multiple fields in both languages
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        doctor.name?.toLowerCase().includes(searchLower) ||
        doctor.name_en?.toLowerCase().includes(searchLower) ||
        doctor.name_ar?.toLowerCase().includes(searchLower) ||
        doctor.specialty?.name?.toLowerCase().includes(searchLower) ||
        doctor.specialty?.name_ar?.toLowerCase().includes(searchLower) ||
        doctor.specialty?.name_en?.toLowerCase().includes(searchLower) ||
        doctor.bio?.toLowerCase().includes(searchLower) ||
        doctor.bio_en?.toLowerCase().includes(searchLower) ||
        doctor.bio_ar?.toLowerCase().includes(searchLower) ||
        doctor.clinic?.name?.toLowerCase().includes(searchLower);
      
      // Client-side specialty filter
      // If doctor has no specialty info (from API), don't filter by specialty - show all
      // This allows the real API data to be shown even when specialty filter is selected
      let matchesSpecialty = true;
      if (selectedSpecialty !== 'all' && doctor.specialty?.id) {
        // Doctor has specialty info - apply filter
        matchesSpecialty = String(doctor.specialty.id) === String(selectedSpecialty) ||
          doctor.specialty?.name?.toLowerCase().includes(
            specialties.find(s => String(s.id) === String(selectedSpecialty))?.name?.toLowerCase() || ''
          );
      }
      
      const matchesCity = selectedCity === 'all' || 
        doctor.city?.toLowerCase() === selectedCity.toLowerCase();
      const matchesRating = doctor.rating >= minRating;
      const matchesPrice = (doctor.consultation_fee || 0) >= priceRange[0] && 
        (doctor.consultation_fee || 0) <= priceRange[1];
      return matchesSearch && matchesSpecialty && matchesCity && matchesRating && matchesPrice;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'rating': return (b.rating || 0) - (a.rating || 0);
        case 'price_low': return (a.consultation_fee || 0) - (b.consultation_fee || 0);
        case 'price_high': return (b.consultation_fee || 0) - (a.consultation_fee || 0);
        case 'experience': return (b.experience_years || 0) - (a.experience_years || 0);
        default: return 0;
      }
    });

  const handleSpecialtyChange = (value) => {
    setSelectedSpecialty(value);
    if (value === 'all') {
      searchParams.delete('specialty');
    } else {
      searchParams.set('specialty', value);
    }
    setSearchParams(searchParams);
  };

  const handleFavoriteClick = (doctor, e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(doctor);
    toast.success(
      isFavorite(doctor.id) 
        ? (lang === 'ar' ? 'تمت الإزالة من المفضلة' : 'Removed from favorites')
        : (lang === 'ar' ? 'تمت الإضافة إلى المفضلة' : 'Added to favorites')
    );
  };

  const clearFilters = () => {
    setSelectedSpecialty('all');
    setSelectedCity('all');
    setMinRating(0);
    setPriceRange([0, 500]);
    setSortBy('rating');
    setSearchQuery('');
    searchParams.delete('specialty');
    setSearchParams(searchParams);
  };

  const activeFiltersCount = [
    selectedSpecialty !== 'all',
    selectedCity !== 'all',
    minRating > 0,
    priceRange[0] > 0 || priceRange[1] < 500,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctors-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="doctors-title">
            {t('doctors.title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">{lang === 'ar' ? 'ابحث واحجز موعدًا مع أفضل الأطباء' : 'Find and book appointments with top healthcare professionals'}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('home.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={selectedSpecialty} onValueChange={handleSpecialtyChange}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="specialty-filter">
                <SelectValue placeholder={t('doctors.filterBySpecialty')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('doctors.allSpecialties')}</SelectItem>
                {specialties.map((specialty) => (
                  <SelectItem key={specialty.id} value={String(specialty.id)}>
                    {specialty.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[150px]" data-testid="sort-filter">
                <SelectValue placeholder={lang === 'ar' ? 'ترتيب حسب' : 'Sort by'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">{lang === 'ar' ? 'التقييم' : 'Rating'}</SelectItem>
                <SelectItem value="price_low">{lang === 'ar' ? 'السعر: الأقل' : 'Price: Low to High'}</SelectItem>
                <SelectItem value="price_high">{lang === 'ar' ? 'السعر: الأعلى' : 'Price: High to Low'}</SelectItem>
                <SelectItem value="experience">{lang === 'ar' ? 'الخبرة' : 'Experience'}</SelectItem>
              </SelectContent>
            </Select>

            {/* Advanced Filters Sheet */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="relative" data-testid="advanced-filters-btn">
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  {lang === 'ar' ? 'فلاتر' : 'Filters'}
                  {activeFiltersCount > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-teal-600">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side={lang === 'ar' ? 'left' : 'right'} className="w-[320px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center justify-between">
                    {lang === 'ar' ? 'الفلاتر' : 'Filters'}
                    {activeFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>
                        <X className="h-4 w-4 mr-1" />
                        {lang === 'ar' ? 'مسح الكل' : 'Clear all'}
                      </Button>
                    )}
                  </SheetTitle>
                </SheetHeader>
                
                <div className="mt-6 space-y-6">
                  {/* City Filter */}
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'المدينة' : 'City'}</Label>
                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger data-testid="city-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CITIES.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {lang === 'ar' ? city.name_ar : city.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rating Filter */}
                  <div className="space-y-3">
                    <Label>{lang === 'ar' ? 'الحد الأدنى للتقييم' : 'Minimum Rating'}</Label>
                    <div className="flex items-center gap-2">
                      {[0, 3, 3.5, 4, 4.5].map((rating) => (
                        <Button
                          key={rating}
                          variant={minRating === rating ? 'default' : 'outline'}
                          size="sm"
                          className={minRating === rating ? 'bg-teal-600 hover:bg-teal-700' : ''}
                          onClick={() => setMinRating(rating)}
                        >
                          {rating === 0 ? (lang === 'ar' ? 'الكل' : 'All') : (
                            <><Star className="h-3 w-3 fill-current mr-1" />{rating}+</>
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Price Range Filter */}
                  <div className="space-y-3">
                    <Label>{lang === 'ar' ? 'نطاق السعر' : 'Price Range'}</Label>
                    <Slider
                      value={priceRange}
                      onValueChange={setPriceRange}
                      min={0}
                      max={500}
                      step={10}
                      className="mt-2"
                    />
                    <div className="flex justify-between text-sm text-slate-500 dark:text-slate-400">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700 mt-4" 
                    onClick={() => setFiltersOpen(false)}
                  >
                    {lang === 'ar' ? 'تطبيق الفلاتر' : 'Apply Filters'}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="container mx-auto px-4 py-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {lang === 'ar' 
            ? `عرض ${filteredDoctors.length} من ${doctors.length} طبيب`
            : `Showing ${filteredDoctors.length} of ${doctors.length} doctors`
          }
        </p>
      </div>

      {/* Doctor List */}
      <div className="container mx-auto px-4 pb-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <Skeleton className="h-24 w-24 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredDoctors.length === 0 ? (
          <div className="text-center py-12" data-testid="no-doctors-message">
            <p className="text-slate-600 mb-4">{t('common.noData')}</p>
            <Button variant="outline" onClick={clearFilters}>
              {lang === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDoctors.map((doctor) => (
              <Card 
                key={doctor.id} 
                className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative"
                data-testid={`doctor-card-${doctor.id}`}
              >
                {/* Favorite Button */}
                <button
                  onClick={(e) => handleFavoriteClick(doctor, e)}
                  className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white shadow-sm transition-colors"
                  data-testid={`favorite-btn-${doctor.id}`}
                >
                  <Heart 
                    className={`h-5 w-5 transition-colors ${
                      isFavorite(doctor.id) 
                        ? 'fill-red-500 text-red-500' 
                        : 'text-slate-400 hover:text-red-400'
                    }`} 
                  />
                </button>

                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <img
                      src={doctor.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=0F6D77&color=fff`}
                      alt={doctor.name}
                      className="h-24 w-24 rounded-xl object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 pr-8">
                        <h3 className="font-semibold text-slate-900 truncate">{doctor.name}</h3>
                      </div>
                      <p className="text-sm text-teal-600 mb-2">{doctor.specialty?.name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium">{doctor.rating}</span>
                        <span className="text-slate-400">({doctor.reviews_count} {t('doctors.reviews')})</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{doctor.experience_years} {t('doctors.years')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between mb-3">
                      {doctor.address && (
                        <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                          <MapPin className="h-3.5 w-3.5" />
                          <span className="truncate">{doctor.address}</span>
                        </div>
                      )}
                      {doctor.consultation_fee > 0 && (
                        <span className="text-sm font-semibold text-teal-600">${doctor.consultation_fee}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" asChild>
                        <Link to={`/doctors/${doctor.id}`}>{t('doctors.viewProfile')}</Link>
                      </Button>
                      <Button size="sm" className="flex-1 bg-teal-600 hover:bg-teal-700" asChild>
                        <Link to={`/doctors/${doctor.id}`}>{t('doctors.bookNow')}</Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorsPage;
